/* © CNES */
import { useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addFilter,
  deleteFilter,
  getFiltersByUser,
  updateFilter,
} from '../../../../../api/filter/Filter';
import { Missions } from '../../../../../assets/config/filters/Referenciel';
import {
  ConfigContext,
  configContextInitialValue,
} from '../../../../../context/ConfigContext';
import { FilterDataContext } from '../../../../../context/FilterDataContext';
import { FilterInitValues } from '../../../../../models/constants/Filter';
import { FilterSaved } from '../../../../../models/FilterSaved';
import {
  getTooltipMessage,
  parseFilterContextToUrl,
} from '../../../../shared/Utils';
import './FilterManageButtons.scss';

export type FilterManageButtonsType = {
  setSelectedMissions: Function;
};

type ToastSeverity = 'success' | 'info' | 'warn' | 'error' | undefined;

/**
 * Filter Buttons to manage context filters for a user logged in.
 * @param setSelectedMissions function setSelectedMissions.
 * @returns FilterUtilButtons Element.
 */
export const FilterManageButtons = ({
  setSelectedMissions,
}: FilterManageButtonsType) => {
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const [showManageDialog, setShowManageDialog] = useState<boolean>(false);
  const [filterName, setFilterName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<string[]>([]);
  const [isDeleteOperation, setIsDeleteOperation] = useState<boolean>(false);

  const { filterData, setFilterData } = useContext(FilterDataContext);
  const isAuthDisabled = process.env.NODE_ENV !== 'production';
  const context = useContext(ConfigContext);
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  /**
   * function called to display toast component
   */
  const displayToast = useCallback(
    (severity: ToastSeverity, summary: string, detail: string) => {
      toast.current?.show({
        severity: severity,
        summary: summary,
        detail: detail,
        life: 2000,
      });
    },
    []
  );

  const isUserNotLogged: boolean = useMemo(() => {
    return !isAuthDisabled && !context.userLoggedIn.email;
  }, [context.userLoggedIn.email, isAuthDisabled]);

  /**
   * UseQuery implementation for add new filter service
   */
  const { refetch: addNewFilter, isSuccess } = useQuery({
    queryKey: [
      'AddUserFilter',
      {
        user: context.userLoggedIn.email,
        name: filterName,
        criteria: filterData,
        url: '',
        notification: '',
      },
      navigate,
      context.setError400,
    ],
    queryFn: addFilter,
    enabled: false,
  });

  /**
   * UseQuery implementation for update filter service
   */
  const { refetch: updateUserFilter, isSuccess: isUpdateSuccess } = useQuery({
    queryKey: [
      'UpdateFilter',
      {
        user: context.userLoggedIn.email,
        name: filterName,
        criteria: filterData,
        url: '',
        notification: '',
      },
      navigate,
      context.setError400,
    ],
    queryFn: updateFilter,
    enabled: false,
  });

  /**
   * UseQuery implementation for get all filter by user
   */
  const { data: userFilterData, refetch } = useQuery<FilterSaved[]>({
    queryKey: [
      'GetUserFilter',
      {
        userEmail: context.userLoggedIn.email,
      },
      navigate,
      context.setError400,
    ],
    queryFn: getFiltersByUser,
    enabled: !isUserNotLogged,
  });

  /**
   * UseQuery implementation for add new filter service
   */
  const { refetch: deleteFilterService, isSuccess: isDeleteSuccess } = useQuery(
    {
      queryKey: [
        'DeleteUserFilter',
        userFilterData &&
          Array.isArray(userFilterData) &&
          userFilterData?.filter((filter) =>
            selectedFilter.includes(filter.name)
          ),
        navigate,
        context.setError400,
      ],
      queryFn: deleteFilter,
      enabled: false,
    }
  );

  /**
   * create ref for isSuccess var to be used inside setTimeOut
   */
  const isSuccessAddRef = useRef(isSuccess);
  isSuccessAddRef.current = isSuccess;
  const isSuccessUpdateRef = useRef(isUpdateSuccess);
  isSuccessUpdateRef.current = isUpdateSuccess;
  const isDeleteSuccessRef = useRef(isDeleteSuccess);
  isDeleteSuccessRef.current = isDeleteSuccess;

  /**
   * called for open or close save dialog
   */
  const handleOpenSaveDialog = () => {
    if (isUserNotLogged) return;
    setShowSaveDialog(!showSaveDialog);
    setFilterName(null);
    setIsDeleteOperation(false);
  };

  /**
   * called for open or close load/delete dialog
   */
  const handleOpenManageDialog = (operation: string) => {
    if (isUserNotLogged) return;
    setIsDeleteOperation(operation === 'delete');
    setShowManageDialog(!showManageDialog);
    setSelectedFilter([]);
  };

  /**
   * onclick reset filter values
   */
  const handleReset = () => {
    setFilterData({ ...FilterInitValues });
    setSelectedMissions(null);
    // set currentView to false to force change detection in map.
    context.setMapExtent({
      extent: configContextInitialValue.mapExtent.extent,
      currentView: false,
      bbox: configContextInitialValue.mapExtent.bbox,
    });

    context.setResetZoneTags();

    const urlParams = parseFilterContextToUrl(
      FilterInitValues,
      context.drawnShape,
      configContextInitialValue.mapExtent.extent
    );
    navigate({
      pathname: urlParams ? '/search' : '/',
      ...(urlParams && { search: urlParams }),
    });
  };

  /**
   * save filter
   */
  const handleSaveFilter = (isUpdate: boolean) => {
    setIsLoading(true);
    if (isUpdate) {
      updateUserFilter()
        .catch(() => {
          displayToast(
            'error',
            'Error',
            'Your search has not been saved. Try again later.'
          );
        })
        .then(() => {
          setShowSaveDialog(false);
          refetch();
          displayToast('success', 'Success', 'Search SAVED!');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      addNewFilter()
        .catch(() => {
          displayToast(
            'error',
            'Error',
            'Your search has not been saved. Try again later.'
          );
        })
        .then(() => {
          setShowSaveDialog(false);
          refetch();
          displayToast('success', 'Success', 'Search SAVED!');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  /**
   * handle Delete Filter.
   */
  const handleDeleteFilter = () => {
    setIsLoading(true);
    deleteFilterService();
    setTimeout(() => {
      setIsLoading(false);
      if (isDeleteSuccessRef.current) {
        setShowManageDialog(false);
        refetch();
        displayToast(
          'success',
          'Success',
          `${selectedFilter.length} Searches DELETED!`
        );
      } else {
        displayToast(
          'error',
          'Error',
          'Your search has not been deleted. Try again later.'
        );
      }
    }, 1000);
  };

  /**
   * handle on click save button
   */
  const handleClickSave = () => {
    if (userFilterData?.find((el) => el.name === filterName)) {
      handleConfirmDialog();
    } else {
      handleSaveFilter(false);
    }
  };

  /**
   * Confirm dialog
   */
  const handleConfirmDialog = () => {
    confirmDialog({
      message: isDeleteOperation
        ? 'Do you want to delete selected saves?'
        : 'A search context with this name already exists. Do you want to overwrite it ?',
      header: 'Confirmation',
      acceptClassName: 'BtnAccept',
      rejectClassName: 'BtnReject',
      accept: isDeleteOperation
        ? () => handleDeleteFilter()
        : () => handleSaveFilter(true),
      reject: () => {},
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      className: 'ConfirmDialogRemove',
      onHide: () => {},
    });
  };

  /**
   * set choosed filter to load
   * @param filter value of selected filter
   */
  const selectOrRemove = (filter: string) => {
    if (isDeleteOperation) {
      const exists = selectedFilter.includes(filter);
      if (exists) {
        setSelectedFilter(selectedFilter.filter((el) => el !== filter));
      } else {
        setSelectedFilter((selectedFilter) => [...selectedFilter, filter]);
      }
    } else {
      setSelectedFilter([filter]);
    }
  };

  /**
   * apply selected filter
   */
  const loadFilter = () => {
    const selectedFilterValue: FilterSaved | undefined = userFilterData?.find(
      (el) => el.name === selectedFilter[0]
    );
    const selectedMissions: number[] | null = [];
    selectedFilterValue?.criteria?.dataset?.includes('PEPS_S1_L1') &&
      selectedMissions.push(Missions.sentinel1);
    selectedFilterValue?.criteria?.dataset?.includes('PEPS_S2_L1C') &&
      selectedMissions.push(Missions.sentinel2);
    if (selectedFilterValue) {
      setFilterData(selectedFilterValue.criteria);
      setSelectedMissions(selectedMissions);
      const urlParams = parseFilterContextToUrl(
        { ...selectedFilterValue.criteria },
        context.drawnShape,
        context.mapExtent.currentView ? context.mapExtent.extent : undefined
      );
      navigate({
        pathname: urlParams ? '/search' : '/',
        ...(urlParams && { search: urlParams }),
      });
    }
    setShowManageDialog(false);
  };

  /**
   * handle load one filter or delete a list of filter
   */
  const handleClickLoadOrDelete = () => {
    if (isDeleteOperation) {
      handleConfirmDialog();
    } else {
      loadFilter();
    }
  };

  return (
    <div className="manage-filter-button">
      <ConfirmDialog />
      <Toast ref={toast} position="top-center" />
      <Dialog
        visible={showManageDialog}
        onHide={() => {
          setShowManageDialog(false);
        }}
        className="load-dialog"
      >
        <div className="load-dialog-body">
          {userFilterData?.length ? (
            userFilterData?.map(({ name }) => {
              return (
                <div className="filter-item" key={name}>
                  <div className="filter-checkbox">
                    <Checkbox
                      inputId={`filterIndex${name}`}
                      value={name}
                      onChange={() => selectOrRemove(name)}
                      checked={selectedFilter.some(
                        (filterName) => filterName === name
                      )}
                      className="filter-checkbox"
                    />
                  </div>
                  <div
                    className={`filter-title ${
                      selectedFilter.some(
                        (filterName) => filterName === name
                      ) && 'checked'
                    }`}
                    onClick={() => selectOrRemove(name)}
                  >
                    <p>{name}</p>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-results">
              <h2>No search saved !</h2>
            </div>
          )}
        </div>
        <div className="load-dialog-action">
          <Button
            role="dialogButton"
            icon={'pi pi-cog'}
            label={isDeleteOperation ? 'Delete selection' : 'Load'}
            className="p-button p-component load-button"
            disabled={!selectedFilter.length}
            onClick={handleClickLoadOrDelete}
            loading={isLoading}
          />
        </div>
      </Dialog>
      <Dialog
        visible={showSaveDialog}
        onHide={handleOpenSaveDialog}
        className="save-dialog"
        header="Save current search context"
      >
        <div className="flex flex-column gap-2">
          <InputText
            placeholder="Name of your search context"
            className="save-dialog-input"
            autoFocus
            onChange={(e) => setFilterName(e.target.value)}
          />
          <Button
            role="saveFilterButton"
            icon={'pi pi-save'}
            label={'Save'}
            className="p-button p-component save-dialog-button"
            disabled={!filterName}
            onClick={handleClickSave}
            loading={isLoading}
          />
        </div>
      </Dialog>
      <Button
        label="Save"
        className={classNames('p-button-sm', {
          disabled: isUserNotLogged,
        })}
        onClick={handleOpenSaveDialog}
        outlined
        tooltip={getTooltipMessage(isUserNotLogged)}
      />
      <Button
        label="Load"
        className={classNames('p-button-sm', {
          disabled: isUserNotLogged,
        })}
        onClick={() => handleOpenManageDialog('load')}
        outlined
        tooltip={getTooltipMessage(isUserNotLogged)}
      />
      <Button
        label="Manage"
        className={classNames('p-button-sm', {
          disabled: isUserNotLogged,
        })}
        onClick={() => handleOpenManageDialog('delete')}
        outlined
        tooltip={getTooltipMessage(isUserNotLogged)}
      />
      <Button
        label="Reset"
        className="p-button-warning p-button-sm"
        onClick={handleReset}
      />
    </div>
  );
};
